/**
 * Forge mobile-first interaction standards.
 *
 * Apple's HIG asks for a 44x44pt minimum hit area and Material asks for 48x48dp;
 * Forge standardises on **48 CSS px on touch viewports**, the stricter of the
 * two, and only allows the compact 28px desktop density from the `sm`
 * breakpoint up, where a precise pointer is the norm.
 *
 * Import these constants instead of re-typing the class strings, so raising the
 * standard later lands on every surface at once. The prose version of the same
 * contract lives in `DESIGN.md` -> "Mobile-First Responsiveness".
 *
 * The classes below use the `touch` (48px) and `no-zoom` (16px) theme tokens
 * declared in `index.css`, not Tailwind's numeric scale. That is deliberate:
 * the scale is rem-based and this app's root font-size is a user-adjustable
 * setting with a 16px default, so `size-11` renders 44px at that default and
 * less at every setting below it, and `text-base` renders 14px here — neither
 * reliably clears the platform minimum it appears to name.
 */

/**
 * Minimum interactive edge length on touch viewports, in CSS px.
 *
 * 48 is the stricter of the two platform minimums Forge web must satisfy at
 * once — Apple HIG 44x44pt, Material 3 48x48dp — and it also clears WCAG 2.2
 * SC 2.5.5 (AAA, 44x44 CSS px).
 */
export const MIN_TOUCH_TARGET_PX = 48;

/**
 * Minimum text size inside a focusable text field on touch viewports, in CSS px.
 * Below this iOS Safari auto-zooms the page on focus and never zooms back out.
 */
export const MIN_TOUCH_FIELD_FONT_SIZE_PX = 16;

/**
 * Icon-only control: a literal 48px square on touch, the compact desktop density
 * from `md` up. Pair with a colour treatment (`variant="ghost"`,
 * `text-muted-foreground`, ...) — this constant only owns sizing, shape, and tap
 * behaviour.
 *
 * The redundant-looking `sm:size-touch` is load-bearing: `Button`'s own size
 * variants carry an `sm:size-8`, and a bare unprefixed `size-touch` loses to it
 * between 640 and 767px. Restating it at `sm` keeps the target at 48px right up
 * to the `md` handover.
 */
export const RESPONSIVE_ICON_BUTTON_CLASS =
  "inline-flex size-touch shrink-0 touch-manipulation items-center justify-center rounded-lg leading-none sm:size-touch md:size-7 md:rounded-md";

/**
 * Hit area for a control that keeps its own width and height: 48px on both
 * axes below `md`, restated at `sm` so the 640 to 767 band cannot demote it.
 *
 * The restatement is the whole point, and it is easy to leave out because the
 * unprefixed token looks like it already covers every width below `md`. It does
 * not, for two different reasons in the two primitives this class is layered
 * over. `Button`'s cva size variants set a fixed `height` (`h-8 sm:h-7`), which
 * a `min-height` still beats, so an unprefixed `min-h-touch` survives there.
 * `SelectTrigger`'s do not: `size="sm"` is `min-h-8 sm:min-h-7`, the same
 * property this class sets, and `sm:min-h-7` sorts after a bare `min-h-touch`
 * in Tailwind's output. A runtime mode select measured 28px at 700px for
 * exactly that reason. Restating at `sm` puts the touch minimum back on the
 * losing side of nothing until `md:` takes over.
 *
 * Sizing only. Pair it with the control's own padding, colour and `md:`
 * density.
 */
export const RESPONSIVE_CONTROL_HIT_AREA_CLASS =
  "min-h-touch min-w-touch touch-manipulation sm:min-h-touch sm:min-w-touch";

/**
 * Icon-only control for dense desktop chrome: the same literal 48px touch
 * target below `md`, with the sidebar's compact `size-5` visual slot once the
 * precise-pointer layout takes over.
 */
export const DENSE_RESPONSIVE_ICON_BUTTON_CLASS =
  "size-touch shrink-0 touch-manipulation rounded-lg sm:size-touch md:size-5 md:rounded-md";

/**
 * Text button: a literal 48px-tall touch target below `md`, then the button's
 * selected compact size on desktop. Restating the token at `sm` prevents a
 * Button size variant from shrinking the box between 640 and 767px.
 */
export const RESPONSIVE_TEXT_BUTTON_CLASS =
  "min-h-touch touch-manipulation sm:min-h-touch md:min-h-0";

/**
 * The invisible `::after` pad of {@link COARSE_POINTER_HIT_AREA_PAD_CLASS},
 * without the coarse-pointer gate, for surfaces with no compact desktop slot
 * to protect: a floating overlay's grab and resize affordances are 48px for
 * every pointer. DESIGN.md -> "Mobile-First Responsiveness": the minimum is
 * user-preference-independent; a Magic Trackpad or touch-screen laptop has a
 * precise pointer and still needs the target.
 *
 * Needs a positioning context on the element: pair it with
 * {@link COARSE_POINTER_HIT_AREA_CLASS} unless the control is already
 * `absolute`/`fixed`.
 */
export const HIT_AREA_PAD_CLASS =
  "after:absolute after:top-1/2 after:left-1/2 after:size-full after:min-h-touch after:min-w-touch after:-translate-x-1/2 after:-translate-y-1/2";

/**
 * The invisible `::after` pad that lifts a visually small control to the full
 * touch minimum on coarse pointers. Needs a positioning context on the element:
 * use {@link COARSE_POINTER_HIT_AREA_CLASS} unless the control is already
 * `absolute`/`fixed` (which establishes one on its own, and where adding
 * `relative` would fight the existing placement).
 */
export const COARSE_POINTER_HIT_AREA_PAD_CLASS =
  "pointer-coarse:after:absolute pointer-coarse:after:top-1/2 pointer-coarse:after:left-1/2 pointer-coarse:after:size-full pointer-coarse:after:min-h-touch pointer-coarse:after:min-w-touch pointer-coarse:after:-translate-x-1/2 pointer-coarse:after:-translate-y-1/2";

/**
 * Keeps a control that must stay visually small (a dense row action, a chip) at
 * the full touch minimum on coarse pointers by projecting an invisible `::after`
 * pad around it. Includes `relative` because the pad needs a positioning context.
 */
export const COARSE_POINTER_HIT_AREA_CLASS = `relative ${COARSE_POINTER_HIT_AREA_PAD_CLASS}`;

/**
 * Text-field sizing that never trips iOS Safari's focus zoom: a literal 16px on
 * touch viewports, back to the app's dense scale from `md` up. Note that
 * `text-base` would NOT do — it is 1rem against a 14px root, i.e. 14px.
 */
export const NO_ZOOM_FIELD_TEXT_CLASS = "text-no-zoom md:text-sm";
